<template>
  <div>
    <section>
      <msp-table data-test-id="msps-data-table" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import { usePageMetaStore } from "@/store/pageMeta.store";
import MspTable from "@/views/System/Overview/MspTable/MspTable.vue";
import { useRoute } from "vue-router";
import { isEmbedded } from "@/remote/contract";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const route = useRoute();

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);

const setRootTitle = () =>
  pageMetaStore.setPageTitle({
    title: t("dashboard"),
  });

const setOrgTitleIfKnown = () => {
  const name =
    selectedMsp.value?.name || selectedAccount.value?.name || t("dashboard");
  pageMetaStore.setPageTitle({ title: name });
};

// Initial title on load
if (!route.params.orgUuid) {
  setRootTitle();
} else {
  setOrgTitleIfKnown();
}

// Watch for route param changes to set selectedMsp
watch(
  () => route.params.orgUuid,
  async (orgUuid) => {
    if (orgUuid) {
      // Set selectedMsp with the UUID; name will be set when org context loads
      await authStore.setMspUser({
        uuid: orgUuid as string,
        name:
          selectedMsp.value?.uuid === orgUuid ? selectedMsp.value?.name : "",
      });
      pageMetaStore.setMSP(selectedMsp.value?.name || "");
      setOrgTitleIfKnown();
    } else {
      // Root view - clear selectedMsp and reset page title
      await authStore.setMspUser(null);
      pageMetaStore.setMSP("");
      setRootTitle();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await authStore.setCustomer(null);
  // Embedded, the System view is an internal, read-only surface inside the host
  // shell; surface that to the user as a subtitle under the page heading.
  if (isEmbedded()) {
    pageMetaStore.setSubtitle(t("system-read-only-subtitle"));
  }
});

onUnmounted(() => {
  pageMetaStore.setSubtitle("");
});
</script>
